/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */



import {Route, UrlMatchResult} from './config';
import {UrlSegment, UrlSegmentGroup} from './url_tree';
/**
 * \@whatItDoes Name of the primary outlet.
 * 
 * \@stable
 */
export const PRIMARY_OUTLET = 'primary';

/**
 * A collection of parameters.
 *
 * @stable
 */
export type Params = {
  [key: string]: any
};

/**
 * Matrix and Query parameters.
 *
 * `ParamMap` makes it easier to work with parameters as they could have either a single value or
 * multiple value. Because this should be known by the user, calling `get` or `getAll` returns the
 * correct type (either `string` or `string[]`).
 *
 * The API is inspired by the URLSearchParams interface.
 * see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 *
 * @stable
 */
export interface ParamMap {
  has(name: string): boolean;
  /**
   * Return a single value for the given parameter name:
   * - the value when the parameter has a single value,
   * - the first value if the parameter has multiple values,
   * - `null` when there is no such parameter.
   */
  get(name: string): string|null;
  /**
   * Return an array of values for the given parameter name.
   *
   * If there is no such parameter, an empty array is returned.
   */
  getAll(name: string): string[];

  /** Name of the parameters */
  readonly keys: string[];
}
class ParamsAsMap implements ParamMap {
private params: Params;
/**
 * @param {?} params
 */
constructor(params: Params) { this.params = params || {}; }
/**
 * @param {?} name
 * @return {?}
 */
has(name: string): boolean { return this.params.hasOwnProperty(name); }
/**
 * @param {?} name
 * @return {?}
 */
get(name: string): string|null {
    if (this.has(name)) {
      const /** @type {?} */ v = this.params[name];
      return Array.isArray(v) ? v[0] : v;
    }

    return null;
  }
/**
 * @param {?} name
 * @return {?}
 */
getAll(name: string): string[] {
    if (this.has(name)) {
      const /** @type {?} */ v = this.params[name];
      return Array.isArray(v) ? v : [v];
    }

    return [];
  }
/**
 * @return {?}
 */
get keys(): string[] { return Object.keys(this.params); }
}

function ParamsAsMap_tsickle_Closure_declarations() {
/** @type {?} */
ParamsAsMap.prototype.params;
}

/**
 * Convert a {\@link Params} instance to a {\@link ParamMap}.
 * 
 * \@stable
 * @param {?} params
 * @return {?}
 */
export function convertToParamMap(params: Params): ParamMap {
  return new ParamsAsMap(params);
}

const /** @type {?} */ NAVIGATION_CANCELING_ERROR = 'ngNavigationCancelingError';
/**
 * @param {?} message
 * @return {?}
 */
export function navigationCancelingError(message: string) {
  const /** @type {?} */ error = Error('NavigationCancelingError: ' + message);
  ( /** @type {?} */((error as any)))[NAVIGATION_CANCELING_ERROR] = true;
  return error;
}
/**
 * @param {?} error
 * @return {?}
 */
export function isNavigationCancelingError(error: Error) {
  return ( /** @type {?} */((error as any)))[NAVIGATION_CANCELING_ERROR];
}
/**
 * @param {?} segments
 * @param {?} segmentGroup
 * @param {?} route
 * @return {?}
 */
export function defaultUrlMatcher(
    segments: UrlSegment[], segmentGroup: UrlSegmentGroup, route: Route): UrlMatchResult|null {
  const /** @type {?} */ parts = /** @type {?} */(( route.path)).split('/');

  if (parts.length > segments.length) {
    // The actual URL is shorter than the config, no match
    return null;
  }

  if (route.pathMatch === 'full' &&
      (segmentGroup.hasChildren() || parts.length < segments.length)) {
    // The config is longer than the actual URL but we are looking for a full match, return null
    return null;
  }

  const /** @type {?} */ posParams: {[key: string]: UrlSegment} = {};

  // Check each config part against the actual URL
  for (let /** @type {?} */ index = 0; index < parts.length; index++) {
    const /** @type {?} */ part = parts[index];
    const /** @type {?} */ segment = segments[index];
    const /** @type {?} */ isParameter = part.startsWith(':');
    if (isParameter) {
      posParams[part.substring(1)] = segment;
    } else if (part !== segment.path) {
      // The actual URL part does not match the config, no match
      return null;
    }
  }

  return {consumed: segments.slice(0, parts.length), posParams};
}



// WEBPACK FOOTER //
// ../../../../packages/router/src/shared.ts